import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { syncResponseSchema } from '@dishly/shared';
import { api } from './api.ts';
import { applyChanges, claimMirror, mirror, readCursor, type Mirror } from './mirror.ts';
import { backoffMs, enqueue, flushOutbox, type NewOp } from './outbox.ts';
import { flushPhotos } from './photo-queue.ts';

/** Pulls every change after the stored cursor into the mirror (several pages if needed). */
export async function pullSync(
  db: Mirror,
  owner: string,
  fetchPage: (since: number) => Promise<unknown> = (since) => api(`/api/sync?since=${since}`),
): Promise<void> {
  await claimMirror(db, owner);
  for (let page = 0; page < 1000; page++) {
    const changes = syncResponseSchema.parse(await fetchPage(await readCursor(db)));
    await applyChanges(db, changes);
    notifyMirrorChange();
    if (!changes.more && !changes.reset) return;
  }
}

let running: Promise<void> | null = null;
let again = false;
let persisted = false;
let currentOwner: string | undefined;
let failures = 0;
let retryTimer: ReturnType<typeof setTimeout> | undefined;

/** Tries the outbox again later, backing off while the server stays out of reach. */
function scheduleRetry(owner: string): void {
  failures++;
  clearTimeout(retryTimer);
  retryTimer = setTimeout(() => void requestSync(owner), backoffMs(failures));
}

/** Whether a request came in during the last pull (and clears it). */
function takeAgain(): boolean {
  const queued = again;
  again = false;
  return queued;
}

/**
 * One sync at a time: send the outbox, then pull. A request while one runs queues exactly one
 * more. Failures (offline) are silent: the ops stay queued and a retry is scheduled.
 */
export function requestSync(owner: string): Promise<void> {
  if (running) {
    again = true;
    return running;
  }
  running = (async () => {
    try {
      again = false;
      do {
        await claimMirror(mirror(), owner);
        // Photos taken offline become ops on their history entries, sent with the rest.
        if (await flushPhotos(mirror())) notifyMirrorChange();
        const sent = await flushOutbox(mirror());
        if (sent) {
          failures = 0;
          clearTimeout(retryTimer);
        } else {
          scheduleRetry(owner);
        }
        await pullSync(mirror(), owner);
      } while (takeAgain());
      if (!persisted) {
        persisted = true;
        // iOS may still evict it; the next open then pulls everything again.
        await navigator.storage.persist().catch(() => false);
      }
    } catch {
      // Offline or signed out: keep the mirror as it is.
    } finally {
      running = null;
    }
  })();
  return running;
}

/** Syncs the signed-in household now (pull to refresh); resolves when done, never throws. */
export function syncNow(): Promise<void> {
  return currentOwner ? requestSync(currentOwner) : Promise.resolve();
}

/** Listeners told whenever the mirror changed here (a queued op or a pull). */
const listeners = new Set<() => void>();

export function onMirrorChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function notifyMirrorChange(): void {
  for (const listener of listeners) listener();
}

/**
 * Records plan or shopping changes (PLAN.md §9): applied to the mirror at once, then sent
 * through the outbox, offline or not.
 */
export async function queueOps(ops: NewOp[]): Promise<void> {
  await enqueue(mirror(), ops);
  notifyMirrorChange();
  if (currentOwner) void requestSync(currentOwner);
}

/**
 * Keeps the mirror fresh while signed in: on open, when the connection returns, when the app
 * comes back to the foreground, after every successful change made here and whenever recipes
 * were fetched from the server.
 */
export function useSync(owner: string | undefined): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    currentOwner = owner;
    if (!owner) return;
    const sync = () => void requestSync(owner);
    const onVisible = () => {
      if (document.visibilityState === 'visible') sync();
    };
    sync();
    window.addEventListener('online', sync);
    document.addEventListener('visibilitychange', onVisible);
    const unsubscribe = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'success') sync();
    });
    // Whenever recipes were (re)fetched, e.g. after an import finished, pull the deltas too.
    const unwatch = queryClient.getQueryCache().subscribe((event) => {
      if (
        event.type === 'updated' &&
        event.action.type === 'success' &&
        (event.query.queryKey as readonly unknown[])[0] === 'recipes'
      ) {
        sync();
      }
    });
    return () => {
      unwatch();
      window.removeEventListener('online', sync);
      document.removeEventListener('visibilitychange', onVisible);
      unsubscribe();
    };
  }, [owner, queryClient]);
}

/**
 * While a shared screen is open (the shopping list, PLAN.md §8), pull every `intervalMs` and when
 * the app regains focus, so two people shopping see each other's checks.
 */
export function useLiveSync(intervalMs = 10_000): void {
  useEffect(() => {
    const sync = () => {
      if (currentOwner && document.visibilityState === 'visible') void requestSync(currentOwner);
    };
    const timer = setInterval(sync, intervalMs);
    window.addEventListener('focus', sync);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', sync);
    };
  }, [intervalMs]);
}
